import StatusBadge from "../components/molecules/StatusBadge";
import type { CustomHighlight } from "../components/organisms/PdfTemplate";
import type { InvoiceReaderTemplateFieldExtractionModel, PositionWithValue, PossibleFieldPositionWithValue, StampInformation } from "../models/ClientInvoiceReaderTemplateModel";
import type { Position } from "../models/Position";
import type { EditableMask, FieldWithUIState } from "../models/Templates";
import type { TemplateMaskInput, TemplateStampInput } from "../models/InvoiceInterpretationInputs";
import { stripTypename } from "./graphql";

export const getTemplateStatusBadge = (
  state: "ACTIVE" | "DISABLED" | "INCOMPLETE" | "UNKNOWN"
) => {
  switch (state) {
    case "ACTIVE":
      return <StatusBadge status="checked" label="common.active" />;
    case "DISABLED":
      return <StatusBadge status="disabled" label="random.disabled" />;
    case "INCOMPLETE":
      return <StatusBadge status="pending" label="random.incomplete" />;
    case "UNKNOWN":
      return <StatusBadge status="info" label="random.unknown" />;
    default:
      return <StatusBadge status="info" label="random.unknown" />;
  }
};

export const getTemplateTypeLabelKey = (
  templateType:
    | "CREDIT"
    | "DEBIT"
    | "CREDIT_ENGLISH"
    | "DEBIT_ENGLISH"
    | "UNKNOWN"
    | "DEBIT_MASK_AND_STAMP_ONLY"
    | "DEBIT_MASK_AND_STAMP_ONLY_ENGLISH"
) => {
  switch (templateType) {
    case "CREDIT":
      return "templates.credit";
    case "DEBIT":
      return "templates.debit";
    case "CREDIT_ENGLISH":
      return "templates.creditEnglish";
    case "DEBIT_ENGLISH":
      return "templates.debitEnglish";
    case "DEBIT_MASK_AND_STAMP_ONLY":
      return "templates.debitMaskAndStampOnly";
    case "DEBIT_MASK_AND_STAMP_ONLY_ENGLISH":
      return "templates.debitMaskAndStampOnlyEnglish";
    case "UNKNOWN":
    default:
      return "random.unknown";
  }
};
export const getTemplateLanguageLabelKey = (
  templateLanguage: "SWEDISH" | "ENGLISH" | "UNKNOWN"
) => {
  switch (templateLanguage) {
    case "SWEDISH":
      return "templates.swedish";
    case "ENGLISH":
      return "templates.english";
    case "UNKNOWN":
    default:
      return "random.unknown";
  }
};

export const getTemplateEInvoiceStatusBadge = (
  state: "NOT_TESTED" | "NOT_WORKING" | "UNKNOWN" | "WORKING"
) => {
  switch (state) {
    case "NOT_TESTED":
      return <StatusBadge status="info" label="random.notTested" />;
    case "NOT_WORKING":
      return <StatusBadge status="failed" label="random.notWorking" />;
    case "WORKING":
      return <StatusBadge status="checked" label="random.working" />;
    case "UNKNOWN":
      return <StatusBadge status="info" label="random.unknown" />;
    default:
      return <StatusBadge status="info" label="random.unknown" />;
  }
};

export const alternativeStampsColorMap = [
  "#4FAE9D",
  "#FA9E14",
  "#00ADEA",
  "#D46286",
  "#C58A84",
  "#FFE4DE",
  "#00296A",
];

export type PositionWithValueAndPage = PositionWithValue & {
  pageNumber: number;
};

export const resolvePositionWithValue = (
  selectedValue: string,
  possibleFields: PossibleFieldPositionWithValue[]
): PositionWithValueAndPage | undefined => {
  const posMatch = selectedValue.match(/^__pos_(\d+)_(.+)__$/);
  if (posMatch) {
    const pageNumber = parseInt(posMatch[1]);
    const key = posMatch[2];
    const field = possibleFields.find((f) => f.position && f.pageNumber === pageNumber && positionKey(f.position) === key);
    if (!field?.position) return undefined;
    return { value: field.value ?? '', position: field.position, pageNumber: field.pageNumber };
  }

  const match = possibleFields.find(
    (f) => f.value === selectedValue && f.position
  );

  if (!match) {
    console.warn("No matching field found for:", selectedValue);
    return undefined;
  }

  return {
    value: match.value ?? '',
    position: match.position!,
    pageNumber: match.pageNumber,
  };
};

export const findPageForPosition = (
  position: Position,
  possibleFields: PossibleFieldPositionWithValue[]
): number | undefined => {
  const match = possibleFields.find(
    (f) =>
      f.position &&
      f.position.bottomLeftX === position.bottomLeftX &&
      f.position.bottomLeftY === position.bottomLeftY &&
      f.position.topRightX === position.topRightX &&
      f.position.topRightY === position.topRightY
  );
  return match?.pageNumber;
};

const isSameFieldAnchor = (a: Position, b: Position) =>
  Math.round(a.bottomLeftX) === Math.round(b.bottomLeftX) &&
  Math.round(a.bottomLeftY) === Math.round(b.bottomLeftY);

export const findAllPagesForField = (
  value: string | undefined,
  position: Position,
  possibleFields: PossibleFieldPositionWithValue[],
): number[] =>
  [...new Set(
    possibleFields
      .filter(
        (f) =>
          f.value === value &&
          f.position &&
          isSameFieldAnchor(f.position, position),
      )
      .map((f) => f.pageNumber),
  )];

export const createHighlight = (label: string, position: Position, pageNumber?: number) => ({
  label,
  position,
  pageNumber,
  color: "rgba(0,123,255,0.8)",
  background: "rgba(0,123,255,0.15)",
});

export const buildMaskFieldHighlights = (
  label: string,
  field: { position: Position; value: string },
  possibleFields: PossibleFieldPositionWithValue[],
  skipRendering: boolean,
): CustomHighlight[] => {
  const pages = findAllPagesForField(field.value, field.position, possibleFields);
  const pageNumbers = pages.length > 0 ? pages : [findPageForPosition(field.position, possibleFields)];

  return pageNumbers.map((pageNumber, i) => ({
    id: `${label}-p${pageNumber ?? "x"}-${i}`,
    label,
    position: field.position,
    pageNumber,
    color: "#E48FB1",
    background: "rgba(255, 160, 200, 0.15)",
    skipRendering,
  }));
};

export const computeHighlightsForMask = (
  mask: EditableMask,
  index: number,
  existingHighlights: CustomHighlight[],
  possibleFields: PossibleFieldPositionWithValue[] = [],
) => {
  const labelLabel = `mask-${index}-label`;
  const valueLabel = `mask-${index}-value`;

  const newHighlights = existingHighlights.filter(
    (h) => h.label !== labelLabel && h.label !== valueLabel,
  );

  if (mask.label?.position) {
    newHighlights.push(
      ...buildMaskFieldHighlights(labelLabel, mask.label, possibleFields, false),
    );
  }

  if (mask.value?.position) {
    newHighlights.push(
      ...buildMaskFieldHighlights(
        valueLabel,
        mask.value,
        possibleFields,
        mask.maskingType !== "REMOVE_LABEL_AND_VALUE",
      ),
    );
  }

  return newHighlights;
};

const round = (n: number) => Math.round(n * 10);

export const buildFieldOptions = (
  possibleFields: PossibleFieldPositionWithValue[],
  totalPages: number | undefined,
  pageLabel: string,
  selectLabel: string,
): Array<{ label: string; value: string; isHeader?: boolean; selectValue?: string }> => {
  if (!totalPages || totalPages <= 1) {
    return [
      { label: selectLabel, value: "" },
      ...possibleFields.map((pf) => ({
        label: pf.value || "",
        value: pf.value || "",
        selectValue: pf.position ? `__pos_${pf.pageNumber}_${positionKey(pf.position)}__` : undefined,
      })),
    ];
  }

  const pages = [...new Set(possibleFields.map((pf) => pf.pageNumber))].sort((a, b) => a - b);
  const result: Array<{ label: string; value: string; isHeader?: boolean; selectValue?: string }> = [
    { label: selectLabel, value: "" },
  ];

  for (const page of pages) {
    result.push({ label: `${pageLabel} ${page}`, value: `__header_${page}__`, isHeader: true });
    possibleFields
      .filter((pf) => pf.pageNumber === page)
      .forEach((pf) => {
        result.push({ label: pf.value || "", value: pf.value || "", selectValue: pf.position ? `__pos_${pf.pageNumber}_${positionKey(pf.position)}__` : undefined });
      });
  }

  return result;
};

export const buildFieldOptionsWithFallback = (
  possibleFields: PossibleFieldPositionWithValue[],
  totalPages: number | undefined,
  pageLabel: string,
  selectLabel: string,
  currentValue: string | null | undefined,
): Array<{ label: string; value: string; isHeader?: boolean; selectValue?: string }> => [
  ...buildFieldOptions(possibleFields, totalPages, pageLabel, selectLabel),
  ...(currentValue && !possibleFields.some((f) => f.value === currentValue)
    ? [{ label: currentValue, value: currentValue }]
    : []),
];

export const getHighlightPageNumber = (
  valueText: string | undefined,
  possibleFields: PossibleFieldPositionWithValue[],
): number | undefined => {
  if (!valueText) return undefined;
  const pages = [...new Set(possibleFields.filter((f) => f.value === valueText).map((f) => f.pageNumber))];
  return pages.length === 1 ? pages[0] : undefined;
};

export const positionKey = (p: Position) =>
  `${round(p.bottomLeftX)}-${round(p.bottomLeftY)}-${round(p.topRightX)}-${round(p.topRightY)}`;

export const hasDuplicatePositions = (
  value: string | undefined,
  possibleFields: PossibleFieldPositionWithValue[],
): boolean => {
  if (!value) return false;
  return possibleFields.filter((f) => f.value === value).length > 1;
};

export const maskingTypeRequiresLabel = (type: EditableMask["maskingType"]) =>
  type === "REMOVE_LABEL_AND_VALUE" ||
  type === "REMOVE_ABSOLUTE_POSITION_ONLY_WHERE_LABEL_EXISTS";

export const toTemplateMaskInput = (mask: EditableMask): TemplateMaskInput | null => {
  if (!mask.value?.position) return null;
  const includeLabel = maskingTypeRequiresLabel(mask.maskingType);
  if (includeLabel && !mask.label?.position) return null;
  return {
    maskingType: mask.maskingType,
    position: stripTypename(mask.value.position),
    label: includeLabel
      ? { value: mask.label!.value, position: stripTypename(mask.label!.position) }
      : undefined,
  };
};

export const toTemplateStampInput = (stamp: StampInformation | undefined): TemplateStampInput | null => {
  if (!stamp?.position) return null;
  return {
    position: stripTypename(stamp.position),
    label: stamp.label?.position
      ? { value: stamp.label.value, position: stripTypename(stamp.label.position) }
      : undefined,
    alternativeStampPositions: stamp.alternativeStampPositions.map(stripTypename),
  };
};

/**
 * Checks if a field (with LABEL or VALUE suffix) is currently highlighted
 */
export const checkIfFieldHighlighted = (
  fieldType: string,
  highlightedFields: CustomHighlight[]
): boolean => {
  const idLabel = `${fieldType}LABEL`;
  const idValue = `${fieldType}VALUE`;
  return highlightedFields.some(
    (highlight) => highlight.label === idLabel || highlight.label === idValue
  );
};

/**
 * Toggles highlight for a field's label and/or value positions
 */
export const toggleHighlightField = (
  fieldType: string,
  highlightedFields: CustomHighlight[],
  setHighlightedFields: (fields: CustomHighlight[]) => void,
  color: string,
  background: string,
  positionLabel?: Position,
  positionValue?: Position,
  pageNumberLabel?: number,
  pageNumberValue?: number,
) => {
  const idLabel = `${fieldType}LABEL`;
  const idValue = `${fieldType}VALUE`;

  if (checkIfFieldHighlighted(fieldType, highlightedFields)) {
    const newHighlights = highlightedFields.filter(
      (highlight) => highlight.label !== idLabel && highlight.label !== idValue
    );
    setHighlightedFields(newHighlights);
  } else {
    let newHighlights: Array<CustomHighlight> = [];
    if (positionLabel) {
      newHighlights.push({
        id: idLabel,
        position: positionLabel,
        label: idLabel,
        pageNumber: pageNumberLabel,
        color,
        background,
      });
    }
    if (positionValue) {
      newHighlights.push({
        id: idValue,
        position: positionValue,
        label: idValue,
        pageNumber: pageNumberValue,
        color,
        background,
      });
    }
    setHighlightedFields([...highlightedFields, ...newHighlights]);
  }
};

/**
 * Synchronizes highlights when field selections change - updates positions while preserving highlight state
 */

export const syncFieldHighlights = (
  fields: Array<{
    fieldType: string;
    label?: { position?: Position; pageNumber?: number };
    value?: { position?: Position; pageNumber?: number }
  }>,
  highlightedFields: CustomHighlight[],
  setHighlightedFields: (fields: CustomHighlight[]) => void,
  color: string,
  background: string,
) => {
  let updated = [...highlightedFields];
  fields.forEach((field) => {
    const idLabel = `${field.fieldType}LABEL`;
    const idValue = `${field.fieldType}VALUE`;
    const labelHighlighted = highlightedFields.some((h) => h.label === idLabel);
    const valueHighlighted = highlightedFields.some((h) => h.label === idValue);

    const fieldIsHighlighted = labelHighlighted || valueHighlighted;

    updated = updated.filter((h) => h.label !== idLabel && h.label !== idValue);

    if (fieldIsHighlighted && field.label?.position) {
      updated.push({
        id: idLabel,
        position: field.label.position,
        label: idLabel,
        pageNumber: field.label.pageNumber,
        color,
        background,
      });
    }
    if (fieldIsHighlighted && field.value?.position) {
      updated.push({
        id: idValue,
        position: field.value.position,
        label: idValue,
        pageNumber: field.value.pageNumber,
        color,
        background,
      });
    }
  });
  setHighlightedFields(updated);
};

export const applyResizeToFields = (
  fields: FieldWithUIState[],
  label: string,
  resolved: { value: string | null; position: Position; pageNumber: number },
): FieldWithUIState[] | null => {
  const safeResolved = { ...resolved, value: resolved.value ?? "" };
  for (let i = 0; i < fields.length; i++) {
    if (label === `${fields[i].fieldType}LABEL`) {
      return fields.map((f, j) => (j === i ? { ...f, label: safeResolved } : f));
    }
    if (label === `${fields[i].fieldType}VALUE`) {
      return fields.map((f, j) => (j === i ? { ...f, value: safeResolved } : f));
    }
  }
  return null;
};

export const applyFieldClick = (
  clickedField: PossibleFieldPositionWithValue,
  activeKey: string | null,
  fields: FieldWithUIState[],
  highlightedFields: CustomHighlight[],
  colorBorder: string,
  colorBackground: string,
): {
  key: string;
  newFields: FieldWithUIState[];
  newHighlights: CustomHighlight[];
  resolved: { value: string; position: Position; pageNumber: number };
} | null => {
  if (!activeKey || !clickedField.position) return null;
  const isLabel = activeKey.endsWith("LABEL");
  const fieldType = activeKey.slice(0, -5);
  const idx = fields.findIndex((f) => f.fieldType === fieldType);
  if (idx === -1) return null;
  const resolved = { value: clickedField.value ?? "", position: clickedField.position, pageNumber: clickedField.pageNumber };
  const newFields = [...fields];
  newFields[idx] = isLabel
    ? { ...newFields[idx], label: resolved, hasNoLabel: false }
    : { ...newFields[idx], value: resolved };
  const idLabel = `${fieldType}LABEL`;
  const idValue = `${fieldType}VALUE`;
  const newHighlights = highlightedFields.filter((h) => h.label !== idLabel && h.label !== idValue);
  if (newFields[idx].label?.position) newHighlights.push({ id: idLabel, label: idLabel, position: newFields[idx].label!.position, pageNumber: newFields[idx].label?.pageNumber, color: colorBorder, background: colorBackground });
  if (newFields[idx].value?.position) newHighlights.push({ id: idValue, label: idValue, position: newFields[idx].value!.position, pageNumber: newFields[idx].value?.pageNumber, color: colorBorder, background: colorBackground });
  return { key: activeKey, newFields, newHighlights, resolved };
};

export const buildFieldsWithPageNumbers = (
  incomingFields: InvoiceReaderTemplateFieldExtractionModel[],
  possibleFields: PossibleFieldPositionWithValue[],
  prevFields: FieldWithUIState[] = [],
): FieldWithUIState[] =>
  incomingFields.map((f) => {
    const prevField = prevFields.find((pf) => pf.fieldType === f.fieldType);
    return {
      ...f,
      hasNoLabel: !f.label,
      label: f.label
        ? {
            ...f.label,
            pageNumber:
              (f.label.position ? findPageForPosition(f.label.position, possibleFields) : undefined)
              ?? prevField?.label?.pageNumber,
          }
        : undefined,
      value: f.value
        ? {
            ...f.value,
            pageNumber:
              (f.value.position ? findPageForPosition(f.value.position, possibleFields) : undefined)
              ?? prevField?.value?.pageNumber,
          }
        : undefined,
    };
  });